import { Roles } from '@common/types/user';
import { ROLE_TO_NAME_MAP } from '@constants/account';
import { useAuth } from '@hooks/useAuth';
import { Alert, Avatar, Button, Typography } from '@mui/material';
import { stringAvatar } from '@utils/common';
import { pathToVP } from '@utils/route';
import { FormattedMessage, useIntl } from 'react-intl';

const Forbidden = () => {
    const { user } = useAuth();
    const intl = useIntl();
    return (
        <>
            <img src="/static/img/logo.png" width={230} />
            <div>
                <Typography
                    fontFamily="museo-sans"
                    fontSize={40}
                    fontWeight={600}
                    mt={5}
                    letterSpacing="-2%"
                    lineHeight="110%"
                >
                    <FormattedMessage
                        id="common.victronProfessional"
                        defaultMessage="Victron <blue>Professional</blue>"
                        values={{
                            blue: (chunks) => (
                                <Typography
                                    component="span"
                                    color="primary"
                                    fontFamily="museo-sans"
                                    fontSize={40}
                                    fontWeight={600}
                                    letterSpacing="-2%"
                                >
                                    {chunks}
                                </Typography>
                            ),
                        }}
                    />
                </Typography>
                <Typography fontSize={16}>
                    <FormattedMessage
                        id="welcome.message"
                        defaultMessage="Welcome to the {brand} Portal"
                        values={{ brand: 'Victron Professional' }}
                    />
                </Typography>
            </div>
            <Alert severity="error" className="mt-8">
                <Typography fontFamily="Roboto" fontWeight={500} fontSize={16} letterSpacing={0.15}>
                    <FormattedMessage
                        id="error.wrongSpot"
                        defaultMessage="Whoops! Looks like you're in the wrong spot!"
                    />
                </Typography>
                <Typography fontFamily="Roboto" fontSize={14} letterSpacing={0.17}>
                    <FormattedMessage
                        id="error.noPermission"
                        defaultMessage="Your current account doesn't have permissions to view this page. To proceed please login with a different account."
                    />
                </Typography>
            </Alert>
            <Typography fontWeight={500} fontSize={15} mt={5}>
                <FormattedMessage id="common.currentUser" defaultMessage="Current logged in user" />
            </Typography>
            <div className="w-full py-8 pl-6 pr-4 bg-neutral-100/50 rounded-md">
                <div className="flex max-h-8 gap-3">
                    <Avatar
                        {...stringAvatar(
                            `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim(),
                            {},
                            'primary-600.main'
                        )}
                        variant="square"
                        className="size-8 rounded-sm"
                    />
                    <div className="flex flex-col">
                        <Typography className="font-semibold leading-4">{user?.email ?? ''}</Typography>
                        <Typography className="font-semibold text-xs text-neutral-800">
                            {(user?.role &&
                                ROLE_TO_NAME_MAP(intl)
                                    .get(user.role || Roles.END_USER)
                                    ?.toUpperCase()) ??
                                ''}
                        </Typography>
                    </div>
                </div>
            </div>
            <div className="my-2 flex flex-col gap-3">
                <Button
                    component="a"
                    href={pathToVP('/accounts/logout').toString()}
                    className="rounded-full black-border"
                    fullWidth
                    size="large"
                    color="primary-600"
                >
                    <Typography fontWeight={500} fontSize={16}>
                        <FormattedMessage
                            id="common.loginWithDifferentAccount"
                            defaultMessage="Login With A Different Account"
                        />
                    </Typography>
                </Button>
                <Button
                    component="a"
                    href={pathToVP().toString()}
                    className="rounded-full border border-solid border-black"
                    fullWidth
                    size="large"
                    sx={{
                        backgroundColor: 'transparent',
                    }}
                    color="inherit"
                >
                    <Typography fontWeight={500} fontSize={16} color="black.main">
                        <FormattedMessage id="common.returnToVP" defaultMessage="Return To Victron Professional" />
                    </Typography>
                </Button>
            </div>
        </>
    );
};

export { Forbidden };
